/**
 * Siqment SaaS Application - Main Entry Point (ES Module)
 */
import { createClient } from '@supabase/supabase-js';
import { AuthManager } from './auth.js';
import { router } from './router.js';
import { OrganizationService } from './services/OrganizationService.js';
import { MappingService } from './services/MappingService.js';
import { appServices } from './services/AppServices.js';
import { createModelsTelemetrySink, modelsTelemetryClient } from './services/ModelsTelemetryClient.js';
import { initMobileNav } from './utils/mobileNav.js';

// =============================================
// KEINE hardcoded Secrets mehr!
// Konfiguration wird vom Backend geladen.
// =============================================

/**
 * Haupt-Anwendungsklasse
 */
class VisualizeApp {
    constructor() {
        this.supabase = null;
        this.authManager = null;
        this.config = null;
        this.initialized = false;
        this.initInProgress = false;
        this.isInitialLoad = true; // Flag für initiale Ladung
        this.appServices = appServices;
        this.modelsTelemetrySink = null;
        this.viewerAllowedRoutes = new Set(['reporting', 'fullscreen']);
        this.appServices.register('app', this);
    }

    t(key, fallback, params = {}) {
        return (window.i18n && window.i18n.t)
            ? window.i18n.t(key, params)
            : fallback;
    }

    /**
     * Lädt die Konfiguration vom Backend
     * KEINE Fallback-Keys mehr - Konfiguration muss vom Server kommen!
     */
    async loadConfig() {
        try {
            const response = await fetch('/api/config');
            
            if (!response.ok) {
                throw new Error(`Server antwortet mit HTTP ${response.status}`);
            }
            
            const config = await response.json();
            
            // Validiere dass die Konfiguration vollständig ist
            if (!config.supabaseUrl || !config.supabaseAnonKey) {
                throw new Error(this.t('app.errors.incompleteConfig', 'Unvollständige Konfiguration vom Server erhalten'));
            }
            
            this.config = config;
            this.appServices.register('config', config);
            return config;
        } catch (error) {
            console.error('❌ Konfiguration konnte nicht geladen werden:', error);
            throw new Error(this.t('app.errors.serverConfigUnavailable', 'Server-Konfiguration nicht verfügbar. Bitte stellen Sie sicher, dass der Server läuft und die Umgebungsvariablen korrekt gesetzt sind.'));
        }
    }

    /**
     * Initialisiert die Anwendung
     */
    async init() {
        if (this.initInProgress || this.initialized) {
            return;
        }
        
        this.initInProgress = true;
        
        try {
            // 1. Konfiguration laden
            await this.loadConfig();
            
            // 3. Supabase initialisieren
            this.initSupabase();
            if (!this.supabase) {
                throw new Error(this.t('app.errors.supabaseInitFailed', 'Supabase konnte nicht initialisiert werden'));
            }

            // 3.5 Telemetry-Sink initialisieren (vor Router/Page-Boot)
            this.initModelsTelemetrySink();
            
            // 4. AuthManager initialisieren
            await this.initAuthManager();
            
            // 5. Router initialisieren (aber noch nicht starten)
            this.initRouter();
            
            // 6. Auth-basierte Navigation einrichten
            this.setupAuthNavigation();
            
            // 7. Event Listeners einrichten
            this.setupEventListeners();
            
            // 8. Notification Styles hinzufügen
            this.injectNotificationStyles();
            
            // 9. Initiale Navigation: Erst Organisation laden, dann Router starten
            await this.handleInitialNavigation();
            
            this.initialized = true;
            this.initInProgress = false;
            
        } catch (error) {
            console.error('❌ [VisualizeApp] Fehler bei der Initialisierung:', error);
            this.initInProgress = false;
            this.showError(this.t('app.errorLoadingApplication', 'Fehler beim Laden der Anwendung. Bitte Seite neu laden.'));
        }
    }

    /**
     * Initialisiert Supabase Client
     */
    initSupabase() {
        if (!this.config) {
            throw new Error(this.t('app.errors.configNotLoaded', 'Konfiguration nicht geladen'));
        }
        
        this.supabase = createClient(
            this.config.supabaseUrl,
            this.config.supabaseAnonKey,
            {
                auth: {
                    persistSession: true,
                    storageKey: 'visualize-supabase-auth',
                    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
                    autoRefreshToken: true,
                    detectSessionInUrl: true
                }
            }
        );
        if (typeof window !== 'undefined') {
            window.supabase = this.supabase;
            window.MappingService = MappingService;
        }
        this.appServices.register('supabase', this.supabase, { exposeToWindow: true, windowKey: 'supabase' });
        this.appServices.register('MappingService', MappingService, { exposeToWindow: true, windowKey: 'MappingService' });
    }

    initModelsTelemetrySink() {
        if (this.modelsTelemetrySink) {
            return this.modelsTelemetrySink;
        }

        this.modelsTelemetrySink = createModelsTelemetrySink({
            resolveAccessToken: async () => {
                if (!this.supabase?.auth?.getSession) {
                    return null;
                }
                try {
                    const sessionResult = await this.supabase.auth.getSession();
                    return sessionResult?.data?.session?.access_token || null;
                } catch (_) {
                    return null;
                }
            }
        });

        if (typeof window !== 'undefined') {
            window.modelsTelemetryClient = modelsTelemetryClient;
        }

        this.appServices.register('modelsTelemetrySink', this.modelsTelemetrySink);
        this.appServices.register('modelsTelemetryClient', modelsTelemetryClient);
        return this.modelsTelemetrySink;
    }

    /**
     * Initialisiert den AuthManager
     */
    async initAuthManager() {
        if (!this.supabase) {
            throw new Error(this.t('app.errors.supabaseNotInitialized', 'Supabase muss zuerst initialisiert werden'));
        }
        
        this.authManager = new AuthManager(this.supabase);
        await this.authManager.init();
        
        // Global verfügbar machen
        window.authManager = this.authManager;
        this.appServices.register('authManager', this.authManager, { exposeToWindow: true, windowKey: 'authManager' });
        this.appServices.register('user', this.authManager.currentUser || null, { exposeToWindow: true, windowKey: 'user' });
    }

    /**
     * Initialisiert den Router
     */
    initRouter() {
        const pageContainer = document.getElementById('page-content');
        
        if (!pageContainer) {
            throw new Error(this.t('app.errors.pageContainerNotFound', 'page-content Container nicht gefunden'));
        }
        
        if (!router) {
            throw new Error(this.t('app.errors.routerNotLoaded', 'Router nicht geladen'));
        }

        router.setServiceContainer(this.appServices);
        router.setPageOptionsResolver(() => ({
            dependencies: this.buildRouteDependencies()
        }));
        
        // Stelle sicher, dass Container leer bleibt bis Auth-Check durchgeführt wurde
        // Zeige Loading-State während Initialisierung
        pageContainer.innerHTML = `
            <div class="loading-state">
                <div class="modern-spinner"></div>
                <p>${this.t('app.loadingApplication', 'Lade Anwendung...')}</p>
            </div>
        `;
        
        // Registriere Routen
        router.register('login', () => import('./pages/LoginPage.js'));
        router.register('data-sources', () => import('./pages/DataSourcesPage.js'));
        router.register('mappings', () => import('./pages/MappingsPage.js'));
        router.register('calculations', () => import('./pages/ModelsPage.js'));
        router.register('reporting', () => import('./pages/SpacesPage.js'));
        router.register('settings', () => import('./pages/SettingsPage.js'));
        router.register('fullscreen', () => import('./pages/FullscreenPage.js'));
        
        // Initialisiere Router (aber noch nicht starten - skipInitialRoute = true)
        router.init(pageContainer, true);
    }

    getOrganizationService() {
        const fromContainer = this.appServices.get('organizationService', null);
        if (fromContainer) {
            return fromContainer;
        }
        if (typeof window !== 'undefined' && window.organizationService) {
            return window.organizationService;
        }
        return null;
    }

    getCurrentOrganizationRole() {
        const organizationService = this.getOrganizationService();
        if (!organizationService) {
            return null;
        }

        if (typeof organizationService.getCurrentOrganizationRole === 'function') {
            return organizationService.getCurrentOrganizationRole();
        }

        const currentOrg = typeof organizationService.getCurrentOrganization === 'function'
            ? organizationService.getCurrentOrganization()
            : null;

        return currentOrg && currentOrg.role
            ? String(currentOrg.role).toLowerCase()
            : null;
    }

    isViewerForCurrentOrganization() {
        return this.getCurrentOrganizationRole() === 'viewer';
    }

    isRouteRestrictedForViewer(path) {
        if (!path) {
            return false;
        }
        return !this.viewerAllowedRoutes.has(path);
    }

    getDefaultRouteForCurrentRole() {
        return this.isViewerForCurrentOrganization() ? 'reporting' : 'data-sources';
    }

    applyRoleBasedNavigationVisibility() {
        const isViewer = this.isViewerForCurrentOrganization();
        document.querySelectorAll('.nav-link').forEach((link) => {
            const page = (link.dataset.page || '').trim();
            const hideForViewer = isViewer && this.isRouteRestrictedForViewer(page);
            link.style.display = hideForViewer ? 'none' : '';
        });

        const roleElement = document.querySelector('.user-role');
        if (roleElement) {
            const role = this.getCurrentOrganizationRole();
            if (role) {
                const labelMap = {
                    admin: 'Admin',
                    owner: 'Owner',
                    editor: 'Editor',
                    member: this.t('app.roles.member', 'Mitglied'),
                    viewer: 'Viewer'
                };
                roleElement.textContent = labelMap[role] || role;
            }
        }
    }

    enforceRoleBasedRouteAccess() {
        if (!this.authManager?.isAuthenticated) {
            return;
        }

        if (!this.isViewerForCurrentOrganization()) {
            return;
        }

        const currentRoute = router.getCurrentRoute();
        if (this.isRouteRestrictedForViewer(currentRoute)) {
            router.navigate('reporting', {}, true);
        }
    }

    buildRouteDependencies() {
        const organizationService = this.getOrganizationService();
        return {
            appServices: this.appServices,
            supabase: this.supabase || this.appServices.get('supabase', null),
            authManager: this.authManager || this.appServices.get('authManager', null),
            organizationService,
            user: this.authManager?.currentUser || this.appServices.get('user', null),
            i18n: this.appServices.get('i18n', null),
            localStorage: typeof window !== 'undefined' ? window.localStorage : this.appServices.get('localStorage', null),
            modelsTelemetrySink: this.appServices.get('modelsTelemetrySink', null)
        };
    }

    /**
     * Richtet Auth-basierte Navigation ein
     */
    setupAuthNavigation() {
        // Registriere beforeNavigate Callback
        router.beforeNavigate((path, params) => {
            const isLoginRoute = router.isLoginRoute(path);
            const isAuthenticated = this.authManager.isAuthenticated;
            
            // Wenn nicht angemeldet und versucht auf geschützte Seite zuzugreifen
            if (!isAuthenticated && !isLoginRoute) {
                // Direkt navigieren, kein setTimeout (verhindert Flackern)
                router.navigate('login', {}, true);
                return false; // Verhindere Navigation
            }
            
            // Wenn angemeldet und versucht auf Login-Seite zuzugreifen
            if (isAuthenticated && isLoginRoute) {
                // Direkt navigieren, kein setTimeout (verhindert Flackern)
                router.navigate(this.getDefaultRouteForCurrentRole(), {}, true);
                return false; // Verhindere Navigation
            }

            if (isAuthenticated && this.isViewerForCurrentOrganization() && this.isRouteRestrictedForViewer(path)) {
                router.navigate('reporting', {}, true);
                return false;
            }
            
            return true; // Erlaube Navigation
        });

        // Registriere Auth State Change Listener
        this.authManager.addListener(async (isAuthenticated, user) => {
            this.appServices.register('user', user || null, { exposeToWindow: true, windowKey: 'user' });
            const currentRoute = router.getCurrentRoute();
            const isOnLoginPage = router.isLoginRoute(currentRoute);
            
            if (!isAuthenticated && !isOnLoginPage) {
                // Abgemeldet und nicht auf Login-Seite -> zu Login
                router.navigate('login');
            } else if (isAuthenticated && isOnLoginPage) {
                // Angemeldet und auf Login-Seite -> zu Datenquellen
                
                // Initialisiere OrganizationService wenn noch nicht geschehen
                if (!this.getOrganizationService()) {
                    await this.initOrganizationService();
                }

                // Optionaler Invite-Token-Flow: #login?invite_token=...
                await this.processPendingInviteToken();
                
                router.navigate(this.getDefaultRouteForCurrentRole());
            }
            
            // UI-Elemente ein-/ausblenden
            this.updateUIForAuthState(isAuthenticated);
        });
    }

    getInviteTokenFromLocation() {
        try {
            const hash = window.location.hash || '';
            const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash;
            const [, hashQueryString = ''] = hashWithoutPrefix.split('?');
            const hashParams = new URLSearchParams(hashQueryString);
            const tokenFromHash = (hashParams.get('invite_token') || '').trim();
            if (tokenFromHash) {
                return tokenFromHash;
            }

            const searchParams = new URLSearchParams(window.location.search || '');
            const tokenFromSearch = (searchParams.get('invite_token') || '').trim();
            return tokenFromSearch || null;
        } catch (error) {
            return null;
        }
    }

    clearInviteTokenFromLocation() {
        try {
            const hash = window.location.hash || '';
            const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash;
            const [hashPath = '', hashQueryString = ''] = hashWithoutPrefix.split('?');
            const hashParams = new URLSearchParams(hashQueryString);
            hashParams.delete('invite_token');
            const nextHash = hashParams.toString() ? `#${hashPath}?${hashParams.toString()}` : `#${hashPath}`;

            const searchParams = new URLSearchParams(window.location.search || '');
            searchParams.delete('invite_token');
            const nextSearch = searchParams.toString();
            const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${nextHash}`;
            window.history.replaceState(null, '', nextUrl);
        } catch (error) {
            // no-op
        }
    }

    async processPendingInviteToken() {
        const inviteToken = this.getInviteTokenFromLocation();
        if (!inviteToken) {
            return;
        }

        const organizationService = this.getOrganizationService();
        if (!organizationService || typeof organizationService.acceptOrganizationInvite !== 'function') {
            return;
        }

        try {
            await organizationService.acceptOrganizationInvite(inviteToken);
            this.showNotification(this.t('app.inviteAccepted', 'Einladung erfolgreich angenommen.'), 'success');
        } catch (error) {
            const errorMessage = error?.message || this.t('app.unknownError', 'Unbekannter Fehler');
            this.showNotification(this.t('app.inviteAcceptError', 'Einladung konnte nicht angenommen werden: {error}', { error: errorMessage }), 'error');
        } finally {
            this.clearInviteTokenFromLocation();
        }
    }

    /**
     * Behandelt die initiale Navigation
     */
    async handleInitialNavigation() {
        const isAuthenticated = this.authManager.isAuthenticated;
        let currentRoute = router.getCurrentRoute();
        const isOnLoginPage = router.isLoginRoute(currentRoute);
        
        // WICHTIG: Prüfe Auth-Status BEVOR Router startet
        // Wenn nicht authentifiziert und nicht auf Login-Seite, navigiere sofort zu Login
        if (!isAuthenticated && !isOnLoginPage) {
            // Setze Hash direkt, damit Router direkt zur Login-Seite navigiert
            window.location.hash = '#login';
            currentRoute = 'login';
        }
        
        // Update UI
        this.updateUIForAuthState(isAuthenticated);
        
        // Initialisiere OrganizationService wenn angemeldet
        if (isAuthenticated && !this.getOrganizationService()) {
            await this.initOrganizationService();
        }

        // Verarbeite Invite-Token auch außerhalb der Login-Route
        // (z. B. nach E-Mail-Confirmation-Redirect mit bestehender Session)
        if (isAuthenticated) {
            await this.processPendingInviteToken();
        }

        if (isAuthenticated && this.isViewerForCurrentOrganization() && this.isRouteRestrictedForViewer(currentRoute)) {
            window.location.hash = '#reporting';
            currentRoute = 'reporting';
        }
        this.applyRoleBasedNavigationVisibility();
        
        // Router starten (nur einmal, nachdem Organisation geladen ist)
        // Dies rendert die initiale Route
        router.start();
        
        // Markiere initiale Ladung als abgeschlossen
        this.isInitialLoad = false;
    }

    /**
     * Aktualisiert UI basierend auf Auth-Status
     */
    updateUIForAuthState(isAuthenticated) {
        const sidebar = document.getElementById('sidebar');
        
        if (sidebar) {
            if (isAuthenticated) {
                sidebar.style.display = '';
                // Lade Profilbild und Namen nach erfolgreichem Login
                this.loadProfileImage();
                this.loadUserName();
                this.applyRoleBasedNavigationVisibility();
            } else {
                sidebar.style.display = 'none';
            }
        }
    }

    /**
     * Lädt den Namen des aktuellen Benutzers aus new_user_organizations und zeigt ihn in der Sidebar an
     */
    async loadUserName() {
        try {
            if (!this.supabase || !this.authManager || !this.authManager.currentUser) {
                return;
            }

            const userNameEl = document.getElementById('user-name');
            if (!userNameEl) {
                return;
            }

            // Warte auf organizationService falls noch nicht verfügbar
            if (!this.getOrganizationService()) {
                let attempts = 0;
                while (!this.getOrganizationService() && attempts < 50) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    attempts++;
                }
            }

            const orgService = this.getOrganizationService();
            if (!orgService) {
                console.warn('⚠️ OrganizationService nicht verfügbar, verwende Fallback');
                // Fallback: Verwende user_metadata
                const metadata = this.authManager.currentUser.user_metadata || {};
                const firstName = metadata.first_name || '';
                const lastName = metadata.last_name || '';
                
                if (firstName || lastName) {
                    const fullName = `${firstName} ${lastName}`.trim();
                    userNameEl.textContent = fullName;
                    return;
                }
                
                const name = this.authManager.currentUser.user_metadata?.name || 
                            this.authManager.currentUser.email?.split('@')[0] || 
                            this.t('app.userFallback', 'Benutzer');
                userNameEl.textContent = name;
                return;
            }

            const userId = this.authManager.currentUser.id;
            let orgId = orgService.getCurrentOrganizationId();
            
            if (!orgId) {
                let attempts = 0;
                while (!orgId && attempts < 50) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    const currentOrgId = orgService.getCurrentOrganizationId();
                    if (currentOrgId) {
                        orgId = currentOrgId;
                        break;
                    }
                    attempts++;
                }
            }

            const finalOrgId = orgService.getCurrentOrganizationId();
            if (!finalOrgId) {
                console.warn('⚠️ Keine Organisation ausgewählt, verwende Fallback');
                // Fallback: Verwende user_metadata
                const metadata = this.authManager.currentUser.user_metadata || {};
                const firstName = metadata.first_name || '';
                const lastName = metadata.last_name || '';
                
                if (firstName || lastName) {
                    const fullName = `${firstName} ${lastName}`.trim();
                    userNameEl.textContent = fullName;
                    return;
                }
                
                const name = this.authManager.currentUser.user_metadata?.name || 
                            this.authManager.currentUser.email?.split('@')[0] || 
                            this.t('app.userFallback', 'Benutzer');
                userNameEl.textContent = name;
                return;
            }

            // Idempotenz: DB-Query für den Namen pro (Benutzer, Organisation) nur einmal.
            // Verhindert wiederholte new_user_organizations-Abfragen bei Auth-/UI-Event-Stürmen.
            const userNameKey = `${userId}|${finalOrgId}`;
            if (this._userNameLoadedKey === userNameKey) {
                return;
            }

            // Lade Namen aus new_user_organizations
            const { data, error } = await this.supabase
                .from('new_user_organizations')
                .select('first_name, last_name')
                .eq('user_id', userId)
                .eq('organization_id', finalOrgId)
                .single();

            // Verarbeitet → nicht erneut abfragen, bis sich Benutzer/Org ändert
            this._userNameLoadedKey = userNameKey;

            if (!error && data) {
                // Verwende Vor- und Nachname falls vorhanden
                const firstName = data.first_name || '';
                const lastName = data.last_name || '';
                
                if (firstName || lastName) {
                    const fullName = `${firstName} ${lastName}`.trim();
                    userNameEl.textContent = fullName;
                    return;
                }
            }

            // Fallback: Lade aus user_metadata
            const metadata = this.authManager.currentUser.user_metadata || {};
            const firstName = metadata.first_name || '';
            const lastName = metadata.last_name || '';
            
            if (firstName || lastName) {
                const fullName = `${firstName} ${lastName}`.trim();
                userNameEl.textContent = fullName;
                return;
            }

            // Letzter Fallback: Email oder Standard
            const name = this.authManager.currentUser.user_metadata?.name || 
                        this.authManager.currentUser.email?.split('@')[0] || 
                        this.t('app.userFallback', 'Benutzer');
            userNameEl.textContent = name;
        } catch (error) {
            console.error('Fehler beim Laden des Benutzernamens:', error);
            // Fallback auf Standard
            const userNameEl = document.getElementById('user-name');
            if (userNameEl && this.authManager && this.authManager.currentUser) {
                const name = this.authManager.currentUser.user_metadata?.name || 
                            this.authManager.currentUser.email?.split('@')[0] || 
                            this.t('app.userFallback', 'Benutzer');
                userNameEl.textContent = name;
            }
        }
    }

    /**
     * Lädt das Profilbild des aktuellen Benutzers und zeigt es in der Sidebar an
     */
    async loadProfileImage(options = {}) {
        const force = !!(options && options.force);

        if (!this.supabase || !this.authManager || !this.authManager.currentUser) {
            return;
        }

        const userId = this.authManager.currentUser.id;

        // Idempotenz: Avatar pro Benutzer nur einmal laden.
        // updateUIForAuthState() und auth.updateUI() rufen diese Methode bei jedem
        // Auth-/UI-Event auf. Ohne diese Guard würde das Bild bei wiederholten Events
        // im Sekundentakt neu angefordert (mit ?t=-Cache-Busting → viele ERR_ABORTED).
        // `force` (nach einem Upload) erzwingt das Neuladen.
        // Diese Guards stehen VOR dem try, damit der finally-Block sie nicht zurücksetzt.
        if (!force && this._avatarLoadedForUserId === userId) {
            return;
        }
        // Re-Entrancy-Guard gegen parallele Aufrufe vor Abschluss des ersten
        if (this._loadingProfileImage) {
            return;
        }
        this._loadingProfileImage = true;

        try {
            const avatarElement = document.querySelector('.user-avatar');
            if (!avatarElement) {
                return; // finally setzt _loadingProfileImage zurück; Retry beim nächsten Event
            }

            // Prüfe ob Profilbild existiert
            const { data, error } = await this.supabase
                .storage
                .from('profile-images')
                .list(userId, {
                    limit: 1,
                    sortBy: { column: 'created_at', order: 'desc' }
                });

            if (error) {
                // Bucket/Bild nicht vorhanden: Standard-Avatar belassen, aber als geladen
                // markieren, damit nicht bei jedem Event erneut gelistet wird.
                this._avatarLoadedForUserId = userId;
                return;
            }

            if (data && data.length > 0) {
                // Hole öffentliche URL
                const { data: urlData } = this.supabase
                    .storage
                    .from('profile-images')
                    .getPublicUrl(`${userId}/${data[0].name}`);

                if (urlData && urlData.publicUrl) {
                    // Cache-Busting nur beim erzwungenen Neuladen (nach Upload), damit das
                    // neue Bild sofort sichtbar ist. Im Normalfall die stabile URL nutzen,
                    // damit der Browser cachen kann und kein Request-Sturm entsteht.
                    const nextSrc = force
                        ? `${urlData.publicUrl}?t=${Date.now()}`
                        : urlData.publicUrl;

                    // Erstelle img Element oder aktualisiere es
                    let img = avatarElement.querySelector('img');
                    if (!img) {
                        // Entferne Emoji falls vorhanden
                        avatarElement.textContent = '';
                        img = document.createElement('img');
                        img.alt = this.t('settings.profileImage', 'Profilbild');
                        avatarElement.appendChild(img);
                    }
                    // Nur setzen, wenn sich die Quelle tatsächlich ändert (verhindert Abort/Reload)
                    if (img.getAttribute('src') !== nextSrc) {
                        img.src = nextSrc;
                    }
                }
            } else {
                // Kein Bild gefunden, zeige Standard-Avatar
                const img = avatarElement.querySelector('img');
                if (img) {
                    img.remove();
                }
                if (!avatarElement.textContent) {
                    avatarElement.textContent = '👤';
                }
            }

            // Erfolgreich verarbeitet → für diesen Benutzer als geladen markieren
            this._avatarLoadedForUserId = userId;
        } catch (error) {
            console.error('Fehler beim Laden des Profilbilds:', error);
        } finally {
            this._loadingProfileImage = false;
        }
    }

    /**
     * Initialisiert den OrganizationService (nur wenn angemeldet)
     */
    async initOrganizationService() {
        try {
            if (!this.supabase || !this.authManager.isAuthenticated) {
                return;
            }

            let organizationService = this.getOrganizationService();
            
            // Prüfe ob Service bereits initialisiert wurde
            if (organizationService && organizationService.isInitialized) {
                return;
            }
            
            // Erstelle OrganizationService nur wenn noch nicht vorhanden
            if (!organizationService) {
                organizationService = new OrganizationService(this.supabase);
                this.appServices.register('organizationService', organizationService, {
                    exposeToWindow: true,
                    windowKey: 'organizationService'
                });
                
                // Registriere Listener VOR init(), damit wir die initiale Benachrichtigung erhalten
                // und überspringen können (wegen isInitialLoad Flag)
                organizationService.addListener((org) => {
                    this.onOrganizationChange(org);
                });
            } else {
                // Aktualisiere Supabase-Referenz falls nötig
                organizationService.supabase = this.supabase;
                this.appServices.register('organizationService', organizationService, {
                    exposeToWindow: true,
                    windowKey: 'organizationService'
                });
            }
            
            // Initialisiere nur wenn noch nicht initialisiert
            if (!organizationService.isInitialized) {
                await organizationService.init();
            }
            
            // Lade Benutzername nach Initialisierung des OrganizationService
            // Kurze Verzögerung, damit die Organisation geladen ist
            setTimeout(() => {
                this.loadUserName();
            }, 200);
            
        } catch (error) {
            console.error('❌ [initOrganizationService] Fehler:', error);
        }
    }

    /**
     * Wird aufgerufen wenn die Organisation geändert wird
     */
    onOrganizationChange(org) {
        const currentRoute = router.getCurrentRoute();
        const newOrgId = (org && org.id) || null;

        // Nur ein *tatsächlicher* Org-Wechsel rechtfertigt teure/destruktive Aktionen.
        // organizationService.notifyListeners() kann mehrfach mit derselben Org feuern;
        // ohne diese Guard würde jede Benachrichtigung loadUserName() (DB-Query) und
        // router.navigate() auslösen und die aktuelle Seite (inkl. Chat-Fenster) im
        // Sekundentakt neu mounten (Chat springt wieder auf "zu").
        const orgActuallyChanged = newOrgId !== this._lastAppliedOrgId;
        this._lastAppliedOrgId = newOrgId;

        // Günstige, idempotente UI-Updates dürfen immer laufen
        this.applyRoleBasedNavigationVisibility();
        this.enforceRoleBasedRouteAccess();

        // Benutzername nur bei echtem Wechsel (DB-Query) oder initialer Ladung neu laden
        if (orgActuallyChanged || this.isInitialLoad) {
            this.loadUserName();
        }

        // Bei initialer Ladung keine Navigation - Router wird bereits gestartet
        if (this.isInitialLoad) {
            return;
        }

        // Seite nur bei echtem Org-Wechsel neu laden (verhindert periodisches Re-Mount)
        if (orgActuallyChanged && ['data-sources', 'mappings', 'calculations', 'reporting'].includes(currentRoute)) {
            // Trigger reload der aktuellen Page
            router.navigate(currentRoute, {}, true);
        }
    }

    /**
     * Richtet Event Listeners ein
     */
    setupEventListeners() {
        // Navigation Links
        document.querySelectorAll('.nav-link').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const page = link.dataset.page;
                if (this.isViewerForCurrentOrganization() && this.isRouteRestrictedForViewer(page)) {
                    router.navigate('reporting');
                    return;
                }
                router.navigate(page);
            });
        });

        // Mobile-Navigation (Drawer). Auf Desktop ohne Wirkung —
        // Button und Overlay sind dort per CSS ausgeblendet.
        initMobileNav();

        // Sidebar Toggle
        const sidebarToggle = document.getElementById('sidebar-toggle');
        const sidebar = document.getElementById('sidebar');
        
        if (sidebarToggle && sidebar) {
            sidebarToggle.addEventListener('click', () => {
                sidebar.classList.toggle('collapsed');
                localStorage.setItem('sidebarCollapsed', sidebar.classList.contains('collapsed'));
            });
            
            const isCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
            if (isCollapsed) {
                sidebar.classList.add('collapsed');
            }
        }

        // Settings Button und Dropdown
        const settingsBtn = document.getElementById('settings-btn');
        const settingsDropdown = document.getElementById('settings-dropdown');
        const logoutBtn = document.getElementById('logout-btn');
        const settingsOpenBtn = document.getElementById('settings-open-btn');
        
        if (settingsBtn && settingsDropdown) {
            // Toggle Dropdown beim Klick auf Settings-Button
            settingsBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                settingsDropdown.classList.toggle('open');
            });
            
            // Schließe Dropdown beim Klick außerhalb
            document.addEventListener('click', (e) => {
                if (!settingsBtn.contains(e.target) && !settingsDropdown.contains(e.target)) {
                    settingsDropdown.classList.remove('open');
                }
            });
        }
        
        // Einstellungen-Button im Dropdown
        if (settingsOpenBtn) {
            settingsOpenBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                settingsDropdown?.classList.remove('open');
                this.openSettingsModal();
            });
        }
        
        // Logout Button im Dropdown
        if (logoutBtn) {
            logoutBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                settingsDropdown?.classList.remove('open');
                this.handleLogout();
            });
        }
    }

    /**
     * Behandelt Logout
     */
    async handleLogout() {
        
        try {
            await this.authManager.logout();
            
            this.showNotification(this.t('app.logoutSuccess', 'Erfolgreich abgemeldet!'), 'success');
            
            // Auth State Change Listener kümmert sich um Navigation zu Login
            // Falls Listener nicht reagiert, navigiere nach kurzer Verzögerung
            setTimeout(() => {
                if (this.authManager && !this.authManager.isAuthenticated) {
                    router.navigate('login');
                }
            }, 100);
                    
        } catch (error) {
            // Wenn Session bereits fehlt, ist das ok - einfach zur Login-Seite
            if (error.message?.includes('Auth session missing')) {
                router.navigate('login');
                return;
            }
            
            console.error('❌ Logout-Fehler:', error);
            this.showNotification(this.t('app.logoutError', 'Fehler beim Abmelden'), 'error');
            
            // Force Navigation zu Login auch bei Fehlern
            router.navigate('login');
        }
    }

    /**
     * Öffnet das Settings-Modal
     */
    openSettingsModal() {
        const modalRoot = document.getElementById('settings-modal-root');
        const SettingsModalComponent = this.appServices.get('SettingsModal', window.SettingsModal);
        const ReactLib = this.appServices.get('React', window.React);
        const ReactDomLib = this.appServices.get('ReactDOM', window.ReactDOM);
        const organizationService = this.getOrganizationService();
        if (!modalRoot || !SettingsModalComponent || !ReactLib || !ReactDomLib) {
            console.error('SettingsModal oder React nicht verfügbar');
            return;
        }

        const handleClose = () => {
            this.closeSettingsModal();
        };

        const handleProfileImageUploaded = () => {
            // Lade Profilbild neu nach erfolgreichem Upload (force = Cache-Busting,
            // umgeht die Idempotenz-Guard, damit das neue Bild sofort erscheint)
            this.loadProfileImage({ force: true });
        };

        ReactDomLib.render(
            ReactLib.createElement(SettingsModalComponent, {
                isOpen: true,
                onClose: handleClose,
                supabase: this.supabase,
                organizationService,
                authManager: this.authManager,
                showNotification: (msg, type) => this.showNotification(msg, type),
                onProfileImageUploaded: handleProfileImageUploaded
            }),
            modalRoot
        );
    }

    /**
     * Schließt das Settings-Modal
     */
    closeSettingsModal() {
        const modalRoot = document.getElementById('settings-modal-root');
        const ReactDomLib = this.appServices.get('ReactDOM', window.ReactDOM);
        if (modalRoot && ReactDomLib) {
            ReactDomLib.unmountComponentAtNode(modalRoot);
        }
    }

    /**
     * Zeigt eine Benachrichtigung
     */
    showNotification(message, type = 'info') {
        let container = document.getElementById('notification-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'notification-container';
            container.className = 'notification-container';
            document.body.appendChild(container);
        }
        
        const notification = document.createElement('div');
        notification.className = `notification ${type}`;
        notification.innerHTML = `
            <div class="notification-content">
                <span class="notification-icon">${type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}</span>
                <span class="notification-text">${message}</span>
            </div>
        `;
        
        container.appendChild(notification);
        
        // Sanftes Ausblenden vor dem Entfernen
        setTimeout(() => {
            if (notification.parentNode) {
                notification.classList.add('fade-out');
                setTimeout(() => {
                    if (notification.parentNode) {
                        notification.remove();
                    }
                }, 300);
            }
        }, 3000);
    }

    /**
     * Zeigt eine Fehlermeldung
     */
    showError(message) {
        const pageContent = document.getElementById('page-content');
        if (pageContent) {
            pageContent.innerHTML = `
                <div class="container">
                    <div class="card">
                        <div class="card__body" style="text-align: center; padding: var(--space-8);">
                            <div style="font-size: 3rem; margin-bottom: var(--space-4);">❌</div>
                            <h3 class="list-title">${this.t('common.error', 'Fehler')}</h3>
                            <p class="list-sub">${message}</p>
                            <button class="btn btn-primary" id="reload-page-btn" style="margin-top: var(--space-4);">${this.t('app.reload', 'Neu laden')}</button>
                        </div>
                    </div>
                </div>
            `;
            
            // Event Listener für Reload-Button
            const reloadBtn = document.getElementById('reload-page-btn');
            if (reloadBtn) {
                reloadBtn.addEventListener('click', () => {
                    location.reload();
                });
            }
        }
    }

    /**
     * Fügt Notification-Styles hinzu
     */
    injectNotificationStyles() {
        if (document.getElementById('notification-styles')) {
            return; // Bereits hinzugefügt
        }

        const notificationStyles = `
.notification-container {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 100000;
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-end;
    gap: 12px;
    pointer-events: none;
}

.notification {
    position: relative;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
    padding: 16px 20px;
    min-width: 320px;
    max-width: 420px;
    pointer-events: auto;
    animation: slideInSoft 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
}

.notification::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, currentColor, transparent);
    opacity: 0.2;
}

.notification.success {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(34, 197, 94, 0.2);
    color: #16a34a;
}

.notification.success::before {
    background: linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.4), transparent);
}

.notification.error {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(239, 68, 68, 0.2);
    color: #dc2626;
}

.notification.error::before {
    background: linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.4), transparent);
}

.notification.info {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(37, 99, 235, 0.2);
    color: #2563eb;
}

.notification.info::before {
    background: linear-gradient(90deg, transparent, rgba(37, 99, 235, 0.4), transparent);
}

.notification-content {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 1;
}

.notification-icon {
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.05));
}

.notification-text {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #1f2937;
    letter-spacing: -0.01em;
}

.notification.success .notification-text {
    color: #15803d;
}

.notification.error .notification-text {
    color: #b91c1c;
}

.notification.info .notification-text {
    color: #1e40af;
}

@keyframes slideInSoft {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.notification:has(+ .notification) {
    margin-bottom: 0;
}

.notification.fade-out {
    animation: slideOutSoft 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes slideOutSoft {
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
    to {
        opacity: 0;
        transform: translateY(-10px) scale(0.95);
    }
}
`;

        const styleSheet = document.createElement('style');
        styleSheet.id = 'notification-styles';
        styleSheet.textContent = notificationStyles;
        document.head.appendChild(styleSheet);
    }
}

export { VisualizeApp };
export default VisualizeApp;
